<template>
  <div
    v-show="submissions.length"
    class="tenant-view-comments"
  >
    <ul class="tenant-view-comments__list">
      <li
        v-for="(item, index) in submissions"
        :key="index"
        class="tenant-view-comments__list--item"
      >
        <a
          :class="{'comments-batch-number__link': item.completedDate, 'comments-batch-number':true }"
          @click="() => item.completedDate && showCommentsDetail(item, submissions.length - 1 - index)"
        >
          {{ item.completedDate ? $t('tenantProjects.tenantViewComments.batchNumberComments', [item.order + 1]) : $t('tenantProjects.tenantViewComments.batchNumber', [item.order + 1]) }}
        </a>
        <div class="comments-batch-date">
          {{ getCommentActionText(item) }}
        </div>
      </li>
    </ul>
    <div
      v-if="isTenant"
      id="showBatchCommentsDetailModal"
      class="reveal"
      data-reveal
    >
      <button
        class="close-button"
        data-close
        aria-label="Close modal"
        type="button"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <CommentsPreviewBody
        :activeBatch="activeBatch"
        :showRecipientsReadOnly="true"
      />
      <TenantSubmissionDocuments
        :files.sync="files"
        :sourceId="(activeBatch || {}).id || ''"
        :canEdit="false"
      />
    </div>
    <EngineerSubmissionModal
      v-else
      ref="engineerSubmissionModal"
      :displayBatchIndex="displayBatchIndex"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters } from 'vuex'
import CommentsPreviewBody from './submissions/CommentsPreviewBody'
import EngineerSubmissionModal from './submissions/EngineerSubmissionModal'
import TenantSubmissionDocuments from './submissions/TenantSubmissionDocuments'
import dateUtil from '@/utils/dateUtil'
import { filterExternalUserVisibleSubmissions } from '@/modules/tenantProjects/utils'
import { CommentActionTypes } from './submissions/consts'

export default {
  name: 'CommentsView',
  components: {
    CommentsPreviewBody,
    EngineerSubmissionModal,
    TenantSubmissionDocuments,
  },
  data () {
    return {
      activeBatch: {},
      dateUtil,
      displayBatchIndex: -1,
      files: [],
    }
  },

  methods: {
    async openPreview () {
      await this.$refs.engineerSubmissionModal.openModal(this.displayBatchIndex)
    },
    async showCommentsDetail (item, index) {
      if (this.isTenant) {
        this.activeBatch = item
        await this.initModal()
        this.showBatchCommentsDetailModal.open()
      } else {
        this.displayBatchIndex = index
        this.openPreview()
      }
    },
    async initModal () {
      if (!this.showBatchCommentsDetailModal) {
        this.showBatchCommentsDetailModal = await new Foundation.Reveal($('#showBatchCommentsDetailModal'), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
    },
    getCommentActionText (submission) {
      if (!submission) {
        return ''
      }
      if (!submission.completedDate) {
        if (this.isTenant) {
          return this.$i18n.t('tenantProjects.submission.comment.underReview')
        } else {
          let startDate =
            (+new Date(deep(submission, 'designComment.startedAt')) || 0) > (+new Date(deep(submission, 'techComment.startedAt')) || 0)
              ? dateUtil.formatDate(deep(submission, 'designComment.startedAt'), true)
              : dateUtil.formatDate(deep(submission, 'techComment.startedAt'), true)
          return `${this.$i18n.t('tenantProjects.submission.startedOn', [startDate])}`
        }
      } else {
        if (submission.completedDate && this.isTenant) {
          const completedDate = dateUtil.formatDate(submission.completedDate, true)
          return `${this.$i18n.t('tenantProjects.submission.receivedOn', [completedDate])}`
        } else {
          const history = submission.actionHistory || []
          const resendHistory = this.getHistoryByStatus(history, CommentActionTypes.RESEND)
          const sendHistory = this.getHistoryByStatus(history, CommentActionTypes.SEND)
          if (resendHistory) {
            const {userName, actionTime} = this.getActionHistoryInfo(resendHistory)
            return `${this.$i18n.t('tenantProjects.submission.resentByOn', [userName, actionTime])}`
          } else if (sendHistory) {
            const {userName, actionTime} = this.getActionHistoryInfo(sendHistory)
            return `${this.$i18n.t('tenantProjects.submission.sentByOn', [userName, actionTime])}`
          } else {
            return ''
          }
        }
      }
    },
    getHistoryByStatus (history, status) {
      return (history).find(rec => rec.actionType === status)
    },
    getActionHistoryInfo (history) {
      const userName = lodash.get(history, 'user.displayName', this.$i18n.t('emptyValue'))
      const actionTime = dateUtil.formatDate(lodash.get(history, 'updatedAt'), true)
      return {userName, actionTime}
    },
  },
  computed: {
    ...mapGetters('tenantProject', ['currentTenantProject', 'currentTenantSubmission']),
    ...mapGetters('user', ['isTenant']),
    tenantCompanyInfo () {
      return deep(this.currentTenantProject, 'tenancy.company') || {}
    },
    submissions () {
      const allSubmissions = deep(this.currentTenantProject, 'submissions') || []
      const submissions = this.isTenant
        ? filterExternalUserVisibleSubmissions(allSubmissions)
        : lodash.filter(allSubmissions, item => item.completedDate)
      return lodash.orderBy(submissions, ['order'], ['desc'])
    },
  },
  destroyed () {
    if (this.showBatchCommentsDetailModal) {
      this.showBatchCommentsDetailModal.destroy()
    }
    // @TODO Replace this modal with <HKLModal />
    $('.reveal-overlay > #showBatchCommentsDetailModal').parent().remove()
  },
}
</script>

<style lang="scss" scoped>

.tenant-view-comments {
  background-color: #fff;
  padding: 10px 10px 0px 10px;
  &__list {
    list-style: none;
    margin-left: 0;
  }
  &__list--item {
    border: 1px solid #e0e1e4;
    display: flex;
    color: #0c3a58;
    justify-content: space-between;
    padding: 9px 14px 11px 17px;
    .comments-batch-number {
      color: #0c3a58;
      font-weight: bold;
      cursor: default;
      &__link {
        text-decoration: underline;
        cursor: pointer;
      }
    }
    .comments-batch-date {
      color: #6d6a64;
      font-style: italic;
      font-size: 12px;
    }
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }
}
</style>

<style lang="scss">
#showBatchCommentsDetailModal  {
  width: 95%;
  overflow: scroll;
  padding: 35px 25px 25px 43px;
}
</style>
